import { all, call, getContext, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import type { EmailChangeDraft, PendingEmailChange } from '../domain/email-change.model';
import { validateDraft } from '../domain/email-change.validation';
import { toEmailChangeError } from '../domain/email-change-error';
import type { EmailChangeGateway } from '../infrastructure/email-change.gateway';
import { cancelRequested, pendingLoadRequested, submitRequested } from './email-change.actions';
import { selectDraft } from './email-change.selectors';
import { emailChangeActions } from './email-change.slice';

/**
 * WORKER: request the change of sign-in email.
 *
 * Validates first (pure domain) and stops without touching the gateway, sparing a
 * round-trip for the two rules this side can decide on its own. Note it validates
 * WITHOUT the current address: that rule needs `AuthContext`, which a saga has no
 * business reading, so the section blocks the button and the backend answers 400;
 * `toEmailChangeError` puts it on the address field either way.
 *
 * The refusals that only the server can decide (a wrong password, 401; an address
 * already used by another account, 409) come back and are placed on the right
 * field the same way.
 */
function* submitWorker() {
  const draft = (yield select(selectDraft)) as EmailChangeDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(emailChangeActions.submitFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: EmailChangeGateway = yield getContext('emailChangeGateway');
  yield put(emailChangeActions.submitStarted());
  try {
    const pending = (yield call(gateway.requestChange, draft)) as PendingEmailChange;
    // The response carries the pending change, so nothing needs reloading; and the
    // banner appears in the same tick the form closes.
    yield put(emailChangeActions.submitSucceeded(pending));
  } catch (e) {
    yield put(emailChangeActions.submitFailed(toEmailChangeError(e)));
  }
}

/** WORKER: read the change awaiting confirmation. */
function* loadPendingWorker() {
  const gateway: EmailChangeGateway = yield getContext('emailChangeGateway');
  yield put(emailChangeActions.pendingLoadStarted());
  try {
    const pending = (yield call(gateway.loadPending)) as PendingEmailChange | null;
    yield put(emailChangeActions.pendingLoaded(pending));
  } catch {
    // Silent: the section has nothing to say about a failed read, and a snackbar on
    // mount for something the user did not ask for is noise. It keeps what it last
    // knew instead of claiming nothing is in flight.
    yield put(emailChangeActions.pendingLoadFailed());
  }
}

/** WORKER: abandon the pending change. */
function* cancelWorker() {
  const gateway: EmailChangeGateway = yield getContext('emailChangeGateway');
  yield put(emailChangeActions.cancelStarted());
  try {
    yield call(gateway.cancelPending);
    // `cancelled: false` (nothing was pending) lands here too, and rightly: the
    // banner should go either way, since there is provably nothing left to confirm.
    yield put(emailChangeActions.cancelSucceeded());
  } catch (e) {
    yield put(emailChangeActions.cancelFailed(toEmailChangeError(e)));
  }
}

/**
 * WATCHERS.
 *
 * `takeLeading` on submit, not `takeLatest`; and the reason is sharper here than
 * for the password. `takeLatest` cancels the previous saga TASK, not the request it
 * already sent, so a double click would fire two POSTs; the second would supersede
 * the first token, invalidating the link that had just been mailed. `takeLeading`
 * ignores the second click until the first finishes, which is what "submit once"
 * actually means.
 *
 * `takeLeading` on cancel for the same reason. `takeLatest` on the read, where the
 * newest answer is the only one worth keeping.
 */
export function* emailChangeSaga() {
  yield all([
    takeLeading(submitRequested.type, submitWorker),
    takeLatest(pendingLoadRequested.type, loadPendingWorker),
    takeLeading(cancelRequested.type, cancelWorker),
  ]);
}
